import { queryOptions, useMutation } from "@tanstack/react-query";

import { sortString } from "@/common/utils/array";
import { getDateFromISOString } from "@/common/utils/date";
import {
  assignCompanyUser,
  CompanyUserAssignRequest,
  createManual,
  getCompany,
  getCompanyUsers,
  SubscriptionCreateManualRequest,
} from "@/generated/api/users";

const selectCompany = (data: Awaited<ReturnType<typeof getCompany>>) => ({
  ...data,
  subscriptions: data.subscriptions.map((s) => ({
    ...s,
    createdAt: getDateFromISOString(s.createdAt),
    expiresAt: getDateFromISOString(s.expiresAt),
  })),
});

const companyQuery = ({ id }: { id: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompany(id, { signal }),
    queryKey: ["company", id],
    select: selectCompany,
  });

const companyUsersQuery = ({ id }: { id: number }) =>
  queryOptions({
    queryFn: ({ signal }) => getCompanyUsers(id, undefined, { signal }),
    queryKey: ["company", id, "users"],
    select: (users) =>
      users.toSorted((a, b) =>
        sortString(a.userEmail ?? "", b.userEmail ?? ""),
      ),
  });

const useUpdateCompanySubscriptionPlanMutation = () =>
  useMutation({
    mutationFn: (payload: SubscriptionCreateManualRequest) =>
      createManual({
        companyId: payload.companyId,
        expiresAt: payload.expiresAt,
        plan: payload.plan,
      }),
  });

const useAssignCompanyUserMutation = () =>
  useMutation({
    mutationFn: (payload: {
      companyId: number;
      companyUserAssignRequest: CompanyUserAssignRequest;
    }) =>
      assignCompanyUser(payload.companyId, payload.companyUserAssignRequest),
  });

type CompanyUser = Awaited<ReturnType<typeof getCompanyUsers>>[number];

export {
  companyQuery,
  companyUsersQuery,
  useAssignCompanyUserMutation,
  useUpdateCompanySubscriptionPlanMutation,
};
export type { CompanyUser };
